import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { URL_SCHEDULES } from '../service/endpoints';
import { deleteData, getData, patchData, postData } from '../service/APIClient';
import dayjs from 'dayjs';

// Thunk to fetch all schedules
export const fetchSchedules = createAsyncThunk(
    'schedules/fetchSchedules',
    async (_, thunkAPI) => {
      let schedules = [];
      let currentPage = 1;
      let totalPages = 1;

      try {
        while (currentPage <= totalPages) {
          const response = await getData(`${URL_SCHEDULES}?page=${currentPage}`)
          if (Array.isArray(response.data)) {
            schedules = [...schedules, ...response.data];
          } else {
            console.error('Expected data to be an array', response.data);
            break;
          }

          totalPages = response.totalPages;
          currentPage++;
        }

        return schedules.map(schedule => ({
          key: schedule?._id?.toString(),
          eventId: schedule?.event?._id,
          startTime: dayjs(schedule?.startTime).toISOString(),
          endTime: schedule?.endTime ? dayjs(schedule?.endTime).toISOString() : null,
          description: schedule?.description,
          templateName: schedule?.templateName,
          customField: schedule?.customField,
          status: schedule?.status
        }));
      } catch (error) {
        console.error('Error fetching schedules:', error);
        return thunkAPI.rejectWithValue(error.response?.data || error.message);
      }
    }
  );

// Thunk to add a new schedule
export const addSchedule = createAsyncThunk(
  'schedules/addSchedule',
  async (newSchedule, thunkAPI) => {
    try {
      return await postData(URL_SCHEDULES, newSchedule);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete a schedule
export const deleteSchedule = createAsyncThunk(
  'schedules/deleteSchedule',
  async (scheduleId, thunkAPI) => {
    try {
      await deleteData(`${URL_SCHEDULES}/${scheduleId}`);
      return scheduleId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const patchSchedules = createAsyncThunk(
  'schedules/patchSchedules',
  async ({ scheduleId, updateData }, thunkAPI) => {
    try {
      const response = await patchData(`${URL_SCHEDULES}/${scheduleId}`, updateData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const schedulesSlice = createSlice({
  name: 'schedules',
  initialState: {
    data: [],
    status: "idle",
    deleteStatus: 'idle',
    error: null,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSchedules.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchSchedules.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(addSchedule.fulfilled, (state, action) => {
        state.data.push(action.payload);
      })
      .addCase(deleteSchedule.pending, state => {
        state.deleteStatus = 'loading';
      })
      .addCase(deleteSchedule.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.data = state.data.filter(
          schedule => schedule._id !== action.payload
        );
      })
      .addCase(deleteSchedule.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload || action.error.message;
      });
  }
});

export const { openModal, closeModal, resetAddStatus } = schedulesSlice.actions;
export default schedulesSlice.reducer;
